/**
 * The FEATURE GATE — web's `helper/featureRestrictionHelper.checkFeature`, moved here verbatim so
 * both platforms answer every control the same way (the adoption's rule: mobile the same as web).
 * Web's app file is now a re-export of this module, so web is unchanged by construction.
 *
 * Differences from web's file, all mechanical:
 *   - the current screen is a parameter (`screen`, defaulting to `currentPosScreen()` from
 *     `utils/navigation`) instead of a `window.location` read, because core cannot read a URL on
 *     mobile;
 *   - `featureRestrictions` / `moduleRestrictions` live in `helpers/commonHelpers` (re-exported
 *     below), because `saleHelpers.showCloseSaleCta` needs them and this module needs saleHelpers;
 *   - the keys are the store-free leaf `./features`.
 *
 * ⚠️ Mobile's app still declares its OWN `checkFeature`, which its cart, table view and new order
 * call until those flows are adopted (docs/ADOPTION_DECISIONS.md D16). My Orders v2 asks THIS one
 * on both platforms. Read `visible` / `enable` by truthiness: web's rules can return `0`,
 * `undefined` or a string.
 */
import { isEmpty } from "lodash";
import { commonConstants, saleHelpers } from "@dotpe/rista-pos-common";
import store from "../../redux-store";
import { isOffline } from "../../facade/ServiceFacade";
import {
  ACCEPTED,
  ACTION_STATES,
  API,
  CANCELLED,
  CLOSED,
  COD,
  CONFIRMED,
  CREATED,
  DELIVERED,
  DISCOUNT_APPROVAL_STAGE,
  DISPATCHED,
  FULFILLMENT_STATUS_TYPES,
  ONLINE,
  ONLINE_CHANNEL,
  OPEN,
  PICKUP,
  POS,
  PROCESSED,
  SALE_RETURN_TYPE,
  SALE_STATUS_TYPES,
  SUPPORT,
  SWIGGY,
  SWIGGY_DINEIN,
  DISTRICT_POSTPAID,
  VOIDED,
  ZOMATO,
} from "../../utils/constants";
import { DAAS_CONSTANTS, DELIVERY, DISCOUNT_CLASS_TYPES, SALE_TRANSACTION_TYPES } from "../../data/constants";
import {
  APPLY_DISCOUNT,
  BILL_SETTLEMENT,
  CANCEL_SALE,
  CLOSE_SALE_BALANCE,
  DEVICES,
  EDIT_AFTER_PRINT,
  EDIT_CHARGE,
  EDIT_CHARGE_AFTER_BILL_PRINT,
  EDIT_CUSTOMER_AFTER_BILL_PRINT,
  EDIT_ORDER_AFTER_BILL_PRINT,
  EDIT_SERVICE_TICKET,
  INVOICE_REPRINT,
  MANUAL_BRANCH_CLOSE,
  MODULES,
  NON_CHARGEABLE_SALE,
  NON_CHARGEABLE_SALE_APPLY,
  OPEN_ORDER_PRINT,
  OVERRIDE_DISCOUNT,
  REPRINT_POST_SETTLEMENT,
  RETURN_SALE,
  SAVE_ORDER,
  SEND_DIGITAL_BILL,
  SPLIT_PAYMENTS,
  TRANSFER_ITEMS,
  UNCANCEL_SALE,
  UNVOID_SALE,
  UPDATE_CLOSED_SALE,
  VOID_SALE,
  REPRINT_KOTS,
  POS_PREFERENCES,
  DISABLE_PAYMENT_SAVE,
} from "../../utils/restrictionConstants";
import { featureRestrictions, getAssignedLicence, moduleRestrictions } from "../commonHelpers";
import { isDAASOrderSource, isOrderCod, isRiderAssignAttempted, isRiderCancelled, isZomatoOrder } from "../myOrderHelpers";
import { isOrderingLicense, showCloseSaleCta } from "../saleHelpers";
import { ORDER_STATUSES } from "../../utils/myOrderConstants";
import { hideCloseOptionForDiscountApproval } from "../hideCloseOptionForDiscountApproval";
import { LICENCE_CODES } from "../../commonComponents/LeftMenu/menuItemsData";
import { isPosChannel, type SaleDataType } from "../../models/sale";
import { USER_ALL_FEATURES } from "../../data/userFeaturesConstants";
import { NC_KEY } from "../../utils/checkoutConstants";
import { getQuickPaymentMethods } from "../paymenthelper";
import { currentPosScreen, type PosScreen } from "../../utils/navigation";
import { FEATURES, type Feature } from "./features";
import { labelsEnabledForSale } from "../../itemLabelSettings/rules";
import { APPROVE_NON_CHARGEABLE_SALE } from "../updateNcApprovalState";

export * from "./features";
export { featureRestrictions, moduleRestrictions } from "../commonHelpers";

const { TagType } = commonConstants;
const swiggyZomatoArr = [ZOMATO, SWIGGY];
const assignRiderStatusesOnline = [ACCEPTED, CONFIRMED, PROCESSED];
const assignRiderStatusesPOS = [CREATED, ACCEPTED, CONFIRMED, PROCESSED];
export const ACCESS_DISABLED_MESSAGE =
  "Current user does not have permission to take this action";

enum Tab {
  MY_ORDER_ROUTE,
  NEW_ORDER_ROUTE,
  TABLE_VIEW_ROUTE,
}

export const apiSupportArr = [API, SUPPORT, DISTRICT_POSTPAID, SWIGGY_DINEIN];
/**
 *
 * @param feature from FEATURES const
 * @param args Record<string, any>
 * @param screen the POS screen the rule is asked from. Web's `checkFeature` read it off the URL;
 *   `currentPosScreen()` still does by default, and my-orders v2 passes `"my-orders"` itself so
 *   both platforms answer from the same branch.
 * @returns true if this feature is allowed
 */
export function checkFeature(
  feature: Feature,
  args: Record<string, any> = {},
  screen: PosScreen = currentPosScreen()
): { visible: boolean; enable: boolean } {
  const currentTab =
    screen === "my-orders" ? Tab.MY_ORDER_ROUTE : screen === "new-order" ? Tab.NEW_ORDER_ROUTE : Tab.TABLE_VIEW_ROUTE;
  const {
    orderData = {},
    currentDiscountApprovalData = [],
    isMoreInfo = false,
    conditions = { visible: true, enable: true },
    disKey = "",
    isDiscountPopup = false,
  } = args;
  // typed here only: core's store types `discounts` as `{}`, which web's looser types never did
  const discountsData: Record<string, any> = store.getState().catalogStore.discounts;
  const checkoutModalStore = store.getState().checkoutModalStore;
  const hasSaleItems = store.getState()?.saleDataStore?.saleData?.itemCount > 0;
  const featureData = store?.getState()?.businessStore?.userData?.featureData;
  function canReprintBill() {
    const settlementFeature = featureData?.find((data: any) => data?.code === REPRINT_POST_SETTLEMENT);
    if (!settlementFeature?.isEnabled) return true;

    const actualCreatedDate = new Date(orderData?.createdDate);
    const expiryTime = new Date(actualCreatedDate.getTime() + settlementFeature?.value * 60 * 60 * 1000);
    const currentTime = new Date();
    return currentTime <= expiryTime;
  }

  const canUpdateClosedSale = updateCloseSaleFeature(orderData);
  const currentApprovalStage =
    currentDiscountApprovalData[0]?.isApprovalRequired ||
    currentDiscountApprovalData[0]?.isRequested ||
    (currentDiscountApprovalData[0]?.requestType === APPROVE_NON_CHARGEABLE_SALE &&
      currentDiscountApprovalData[0]?.currentStatus === DISCOUNT_APPROVAL_STAGE.REJECTED);
  const checkForBackOrderPayment =
    orderData?.backOrderItemCount > 0 && orderData?.saleStatus === "Open";
  const checkForBackOrderState =
    orderData?.backOrderItemCount > 0 &&
    (orderData?.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.CREATED ||
      orderData?.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.CONFIRMED);
  const canCheckoutAndApplyDiscount =
    currentTab === Tab.NEW_ORDER_ROUTE ||
    checkForDiscountAndCheckout(orderData);
  const isNCSale = orderData.transactionType === NC_KEY;
  const hasItems = orderData?.saleItems ? orderData?.saleItems?.length > 0 :true;
  const hasPaymentModesPermitted = getQuickPaymentMethods()?.length ? true : false ;
  // order is considered saved if either version (for BE) or cversion (for offline mode) is set
  const hasSavedVersion = !!orderData?.version || !!orderData?.cversion;
  const isDineInAggregator = [DISTRICT_POSTPAID, SWIGGY_DINEIN].includes(orderData?.sourceInfo?.source?.toLowerCase());
  switch (feature) {
    case FEATURES.CUSTOM: {
      return conditions;
    }
    case FEATURES.TRANSFER_TABLE: {
      return {
        visible: !(orderData?.printed || orderData?.printCount > 0),
        enable: featureRestrictions(TRANSFER_ITEMS),
      };
    }
    case FEATURES.TRANSFER_ITEMS:
      return {
        visible: true,
        enable: featureRestrictions(TRANSFER_ITEMS),
      };
    case FEATURES.FULL_PAYMENT: {
      return {
        visible: !checkoutModalStore.hideFullPayment,
        enable: true,
      };
    }
    case FEATURES.SPLIT_PAYMENT: {
      return {
        visible: true,
        enable: featureRestrictions(SPLIT_PAYMENTS),
      };
    }
    case FEATURES.PARTIAL_PAYMENT: {
      return {
        visible:
          !checkoutModalStore.hideFullPayment && !checkoutModalStore.returnSale,
        enable: featureRestrictions(CLOSE_SALE_BALANCE),
      };
    }
    case FEATURES.NC_SALE: {
      return {
        //check if this is not a return sale and it is not closed order
        visible: !checkoutModalStore.returnSale && orderData.saleStatus !== SALE_STATUS_TYPES.CLOSED,
        enable: featureRestrictions(NON_CHARGEABLE_SALE) || featureRestrictions(NON_CHARGEABLE_SALE_APPLY),
      };
    }
    case FEATURES.CUSTOMER_INFO:
    case FEATURES.ACTIVITY_LOG:
    case FEATURES.ALLOWED: {
      return { visible: true, enable: true };
    }
    case FEATURES.OVERRIDE_DISCOUNT:
      return {
        visible:
          discountsData?.[disKey]?.dclass === DISCOUNT_CLASS_TYPES.discount,
        enable:
          featureRestrictions(OVERRIDE_DISCOUNT) && discountsData?.[disKey]?.overrideDiscount, 
      };
    case FEATURES.ONLINE_ORDER_NOTIFICATION:
      return {
        visible: true,
        enable: !isOrderingLicense(),
      };
    case FEATURES.CHANGE_BRANCH:
      let license = getAssignedLicence();
      let allowedLicenseList = [
        LICENCE_CODES.POS,
        LICENCE_CODES.SEP,
        LICENCE_CODES.SLT,
        LICENCE_CODES.POO,
        LICENCE_CODES.SLF,
      ];
      return {
        visible: !isOffline(),
        enable:
          (!license?.length || allowedLicenseList.includes(license)) &&
          featureRestrictions(DEVICES) &&
          moduleRestrictions(MODULES.access),
      };
    case FEATURES.BRANCH_CLOSE:
      return {
        visible: true,
        enable: featureRestrictions(MANUAL_BRANCH_CLOSE),
      };
    case FEATURES.EDIT_CUSTOMER:
      return {
        visible: true,
        enable:
          orderData?.printCount > 0
            ? featureRestrictions(EDIT_CUSTOMER_AFTER_BILL_PRINT)
            : true,
      };
    case FEATURES.HOLD_ORDER:
      return {
        visible: !currentApprovalStage,
        enable:
          featureRestrictions(SAVE_ORDER) &&
          (orderData?.printCount
            ? featureRestrictions(EDIT_ORDER_AFTER_BILL_PRINT)
            : true),
      };
    case FEATURES.EDIT_CHARGES:
      return {
        visible: orderData?.saleItems?.length > 0,
        enable:
          featureRestrictions(EDIT_CHARGE) &&
          (orderData?.printCount > 0
            ? featureRestrictions(EDIT_CHARGE_AFTER_BILL_PRINT)
            : true),
      };
    case FEATURES.OVERRIDE_ITEM_NAME:
      return {
        visible: true,
        enable: true,
      };
    case FEATURES.BACK_ORDER:
      return {
        visible:
          (store.getState().businessStore?.businessData?.checkInventoryOnSale &&
            featureRestrictions(USER_ALL_FEATURES.BACK_ORDER_FEATURE)) ||
          false,
        enable: true,
      };
    case FEATURES.SALE_BY:
      return {
        visible:
          isPosChannel(orderData.channel) &&
          orderData.saleStatus?.toLowerCase() === OPEN,
        enable: true,
      };
    case FEATURES.HELP_CTA:
      return {
        visible:
          orderData.channel?.toLowerCase() === ONLINE &&
          swiggyZomatoArr.includes(orderData.sourceInfo?.source),
        enable: true,
      };
    case FEATURES.SALE_RETURN:
      return {
        visible:
          orderData?.saleStatus === SALE_STATUS_TYPES.CLOSED &&
          orderData?.channel !== ONLINE_CHANNEL &&
          (!!Object.keys(orderData?.returnInfo || {})?.length ||
            !orderData?.originalSaleInfo),
        enable: featureRestrictions(RETURN_SALE),
      };
    case FEATURES.OUTLET_STATUS:
      return {
        visible: orderData.channel?.toLowerCase() === ONLINE,
        enable: true,
      };
    case FEATURES.ASSIGN_RIDER: {
      return {
        visible: checkAssignRiderCta(orderData),
        enable: true,
      }
    }
    case FEATURES.CANCEL_RIDER:
      return { visible: checkForCancelRider(orderData), enable: true };
    case FEATURES.UNCANCEL_SALE:
      return {
        visible:
          orderData.saleStatus?.toLowerCase() === ORDER_STATUSES.CANCEL &&
          !orderData?.deliveryInfo?.advanceOrder &&
          !isOffline() &&
          !orderData?.serviceLabel?.isAdhoc,
        enable: featureRestrictions(UNCANCEL_SALE),
      };
    case FEATURES.UNVOID_SALE:
      return {
        visible:
          orderData.saleStatus?.toLowerCase() === ORDER_STATUSES.VOID &&
          !isOffline() &&
          !orderData?.deliveryInfo?.advanceOrder,
        enable: featureRestrictions(UNVOID_SALE),
      };
    case FEATURES.VOID_SALE:
      return {
        visible: showVoidCta(orderData),
        enable: featureRestrictions(VOID_SALE),
      };
    case FEATURES.CANCEL_SALE:
      return {
        visible:
          ((args?.isDeleteOrderCta || args?.isCancelOrderCta) && hasSavedVersion) && (isPosChannel(orderData.channel) &&
            orderData.saleStatus?.toLowerCase() === OPEN) ||
          checkAPISupportOrder(orderData),
        enable: featureRestrictions(CANCEL_SALE) && hasSavedVersion,
      };
    case FEATURES.SALE_REOPEN:
      return {
        visible:
          !isOffline() &&
          orderData?.saleStatus === SALE_STATUS_TYPES.CLOSED &&
          isPosChannel(orderData?.channel) &&
          (orderData?.otherInfo?.reopenCount || 0 ) < 1 &&
          orderData?.transactionType === SALE_TRANSACTION_TYPES.SALE &&
          isEmpty(orderData?.splits) &&
          !orderData?.preventEdit,
        enable: featureRestrictions(USER_ALL_FEATURES.ALLOW_SALE_REOPEN),
      };
    case FEATURES.CLOSE_SALE:
      let isPrinted = orderData?.printed || orderData?.printCount > 0;
      return {
        visible:
          currentTab === Tab.NEW_ORDER_ROUTE
            ? hideCloseOptionForDiscountApproval(currentDiscountApprovalData[0])
            : currentTab === Tab.TABLE_VIEW_ROUTE
            ? isMoreInfo && hideCloseOptionForDiscountApproval(currentDiscountApprovalData[0])
              ? !isPrinted
              : isPrinted
            : ((isPosChannel(orderData.channel) &&
                orderData.saleStatus?.toLowerCase() === OPEN) ||
                checkAPISupportOrder(orderData)) &&
              hideCloseOptionForDiscountApproval(currentDiscountApprovalData[0]),
        enable:
          // featureRestrictions(EDIT_AFTER_PRINT)
          featureRestrictions(BILL_SETTLEMENT) &&
          showCloseSaleCta(currentDiscountApprovalData[0], false),
      };
    case FEATURES.REPRINT_KOT:
      return {
        visible: true,
        enable:featureRestrictions(REPRINT_KOTS),
      };
    case FEATURES.CANCEL_KOT:
      let editCondition =
        orderData?.printCount > 1
          ? featureRestrictions(EDIT_AFTER_PRINT)
          : true;
      return {
        visible: orderData?.saleStatus !== SALE_STATUS_TYPES.CLOSED,
        enable: featureRestrictions(EDIT_SERVICE_TICKET) && editCondition,
      };
    case FEATURES.FEEDBACK_CTA:
      return { visible: checkForFeedbackCta(orderData), enable: true };
    case FEATURES.SAVE:
      return {
        visible: !currentApprovalStage && !checkoutModalStore.returnSale,
        enable:  hasItems && (featureRestrictions(SAVE_ORDER)|| !featureRestrictions(DISABLE_PAYMENT_SAVE)) && hasSaleItems,
      };
    case FEATURES.RETURN_OTP:
      return {
        visible:
          orderData?.sourceInfo?.returnOTP &&
          orderData?.fulfillmentStatus === ACTION_STATES.RETURN_INITIATED,
        enable: true,
      };
    case FEATURES.REQUEST_APPROVAL:
      return {
        visible: canCheckoutAndApplyDiscount && currentApprovalStage,
        enable: true,
      };
    case FEATURES.MARK_READY:
      return {
        visible:
          canCheckoutAndApplyDiscount &&
          checkForBackOrderState &&
          (!!checkForBackOrderPayment ||
            !!orderData?.paymentInfo?.balanceAmount ||
            checkWebhookOrder(orderData)),
        enable: canUpdateClosedSale,
      };
    case FEATURES.DISCOUNT:
      return {
        // dev aa950a7fd8: the screen test is PARENTHESISED. Without them `? :` bound looser than
        // the `&&` chain, so off New Order the whole gate collapsed to `isMoreInfo || balance`
        // and a cancelled sale with a balance showed Discount. Also no discount on an NC sale.
        visible:
          canCheckoutAndApplyDiscount &&
          !isNCSale &&
          !isOrderCod(orderData) &&
          !isZomatoOrder(orderData) &&
          orderData.sourceInfo?.source?.toLowerCase() !== SWIGGY &&
          (currentTab === Tab.NEW_ORDER_ROUTE
            ? true
            : isMoreInfo || !!orderData?.paymentInfo?.balanceAmount),
        enable:
          hasItems &&
          checkEditDiscount(orderData) &&
          canUpdateClosedSale &&
          featureRestrictions(APPLY_DISCOUNT),
      };
    case FEATURES.PRINT_BILL:
      return {
        visible:
          orderData?.saleStatus?.toLowerCase() !== CANCELLED.toLowerCase() &&
          checkForPrintBill(orderData),
        enable:
          (orderData?.saleStatus === SALE_STATUS_TYPES.OPEN
            ? featureRestrictions(OPEN_ORDER_PRINT)
            : true) &&
          (orderData?.printCount > 0
            ? featureRestrictions(INVOICE_REPRINT)
            : true) &&
          (orderData?.paymentInfo?.balanceAmount === 0
            ? featureRestrictions(REPRINT_POST_SETTLEMENT) && canReprintBill()
            : true),
      };
    case FEATURES.QUICK_SETTLE:
      return {
        visible:
          hasPaymentModesPermitted &&
          (currentTab === Tab.NEW_ORDER_ROUTE
            ? true
            : !!checkForBackOrderPayment ||
              !!orderData?.paymentInfo?.balanceAmount ||
              checkWebhookOrder(orderData)) &&
          isShowQuickPayment(orderData) &&
          canCheckoutAndApplyDiscount &&
          !currentApprovalStage &&
          !checkForBackOrderState &&
          hideCloseOptionForDiscountApproval(currentDiscountApprovalData[0]),
        enable:
          hasItems &&
          featureRestrictions(BILL_SETTLEMENT) &&
          showCloseSaleCta(currentDiscountApprovalData[0]),
      };
    case FEATURES.CHECKOUT:
      //for closed order, only show save order in discount popup
      if (isDiscountPopup && orderData?.saleStatus === SALE_STATUS_TYPES.CLOSED) {
        return {
          visible: false,
          enable: false,
        }
      }
      const checkForBalance = isMoreInfo || !!orderData?.paymentInfo?.balanceAmount;
      return {
        visible:
          !isNCSale &&
          !currentApprovalStage &&
          (currentTab === Tab.NEW_ORDER_ROUTE
            ? true
            : !!checkForBackOrderPayment ||
              checkForBalance ||
              checkWebhookOrder(orderData)) &&
          canCheckoutAndApplyDiscount &&
          (isMoreInfo ? orderData?.transactionType !== SALE_RETURN_TYPE : true),
        enable: hasItems && canUpdateClosedSale && featureRestrictions(BILL_SETTLEMENT) && hasSaleItems,
      };
    case FEATURES.FULFILLMENT_CTA:
      const visible = (orderData?.fulfillmentStatus !== FULFILLMENT_STATUS_TYPES.DELIVERED || 
        orderData?.saleStatus===VOIDED) &&
        (showInstoreCodCta(orderData) ||
          checkForCodZomato(orderData) ||
          showZomatoSwiggyCta(orderData) ||
          (isPosChannel(orderData.channel) &&
            orderData?.deliveryInfo?.mode));
      if (!isDAASOrderSource(orderData)) {
        return {
          visible,
          enable: true,
        }
      }
      /* DAAS order is source
        check if DAAS flow is triggered; */
      const isDaasFlowTriggered = isRiderAssignAttempted(orderData) && !isRiderCancelled(orderData);
      // if processesed -> disable for DAAS triggered orders. 
      const isMarkedFoodReady = [PROCESSED, DISPATCHED, DELIVERED].includes(orderData?.fulfillmentStatus || "");
      return {
        visible,
        enable: !isDaasFlowTriggered || !isMarkedFoodReady,
      };
    case FEATURES.PROCESSING_TIME:
      return {
        visible:
          (!saleHelpers.getCommonOrderTag(orderData, TagType.ZOMATO_EXPRESS) && saleHelpers.isZomatoScheduledOrder(orderData) && orderData?.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.CREATED) ||
          (orderData.sourceInfo?.source === SWIGGY &&
            orderData?.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.CREATED &&
            !saleHelpers.getCommonOrderTag(orderData, TagType.SWIGGY_BOLT)),
        enable: true,
      };
    case FEATURES.UNABLE_TO_ACCEPT:
      return {
        visible:
          orderData.sourceInfo?.source?.toLowerCase() === SWIGGY &&
          orderData?.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.CREATED &&
          orderData?.saleStatus !== ACTION_STATES.CANCELLED,
        enable: true,
      };
    case FEATURES.SEND_DIGITAL_BILL:
      return {
        visible:
          !isOffline() && currentTab === Tab.TABLE_VIEW_ROUTE
            ? isMoreInfo
              ? !(orderData?.printed || orderData?.printCount > 0)
              : orderData?.printed || orderData?.printCount > 0
            :
            !(swiggyZomatoArr.includes(orderData.sourceInfo?.source)),
        enable: featureRestrictions(SEND_DIGITAL_BILL),
      };
    case FEATURES.GENERATE_BILL:
      return {
        visible: isDineInAggregator,
        enable: true,
      };
    case FEATURES.CALL:
      return {
        visible: orderData.channel?.toLowerCase() === ONLINE && //should be online order
          //if it is swiggy order, should not be delivered
          (!saleHelpers.isSwiggyOrder(orderData) || orderData.fulfillmentStatus !== FULFILLMENT_STATUS_TYPES.DELIVERED),
        enable: true,
      };
    case FEATURES.MARK_OOS:
      return { visible: true, enable: true };
    case FEATURES.ACCEPT:
      return { visible: true, enable: true };
    case FEATURES.REJECT:
      return { visible: checkForRejectBtn(orderData), enable: true };
    case FEATURES.EDIT_ITEMS:
      return {
        visible: checkForEditBtn(orderData),
        enable: checkEditDiscount(orderData),
      };
    case FEATURES.PRINT_ITEM_LABEL_STICKER:
      return {
        visible: checkForPrintLabelSticker(orderData),
        enable: true,
      }
    case FEATURES.POS_PREFERENCES:
      return {
        visible: featureRestrictions(POS_PREFERENCES),
        enable: featureRestrictions(POS_PREFERENCES),
      }
    case FEATURES.DELETE_CTA:
      // order must have at least one item to allow deletion
      const hasOrderItems = orderData?.saleItems?.length > 0;
      // allow deletion if not triggered from delete-order CTA, or if the order hasn't been saved yet
      const canDeleteOrder = !args?.isDeleteOrderCta || !hasSavedVersion;
      return {
        visible: !args?.isDeleteOrderCta || !hasSavedVersion,
        enable: hasOrderItems && canDeleteOrder
      }
    default:
      return { visible: false, enable: false };
  }
}

function checkForPrintBill(orderData: Record<string, any> = {}) {
  if (
    orderData?.channel?.toLowerCase() === ONLINE &&
    orderData.fulfillmentStatus === CREATED &&
    !orderData.paymentInfo?.balanceAmount
  )
    return false;
  return true;
}

const checkForRejectBtn = (orderData: Record<string, any> = {}) => {
  if (orderData.sourceInfo?.source?.toLowerCase() === SWIGGY) {
    return false;
  }
  // check if it is zomato scheduled order and isCancellaable flag in sourceInfo
  if (saleHelpers.checkZomatoScheduledReject(orderData)) {
    return false;
  }
  let { fulfillmentStatus, sourceInfo, channel, saleStatus } = orderData,
    onlineReceivedNotConfirmed =
      channel?.toLowerCase() === ONLINE &&
      saleStatus?.toLowerCase() === OPEN &&
      (fulfillmentStatus === CREATED || fulfillmentStatus === CONFIRMED);
  if (
    onlineReceivedNotConfirmed &&
    sourceInfo?.actions?.reject?.allowedStatus?.length
  ) {
    if (
      sourceInfo?.actions?.reject?.allowedStatus?.includes(fulfillmentStatus)
    ) {
      return true;
    }
  }
  if (
    onlineReceivedNotConfirmed &&
    sourceInfo?.external &&
    !sourceInfo?.actions?.reject
  ) {
    return true;
  }
  if (orderData?.channel === POS) {
    if (
      orderData?.channel?.toLowerCase() === ONLINE &&
      !orderData?.nextAction &&
      orderData?.fulfillmentStatus === CREATED &&
      orderData?.saleStatus?.toLowerCase() !== CANCELLED.toLowerCase()
    ) {
      return true;
    }
  }
  return onlineReceivedNotConfirmed;
};

function updateCloseSaleFeature(orderData: Record<string, any> = {}) {
  return (
    orderData?.saleStatus?.toLowerCase() !== CLOSED ||
    featureRestrictions(UPDATE_CLOSED_SALE)
  );
}

function checkEditDiscount(orderData: Record<string, any>) {
  return (
    (orderData?.printCount > 0
      ? featureRestrictions(EDIT_AFTER_PRINT)
      : true) &&
    (orderData?.printCount > 0
      ? featureRestrictions(EDIT_ORDER_AFTER_BILL_PRINT)
      : true)
  );
}

function isShowQuickPayment(orderData: Record<string, any>) {
  const businessData = store.getState()?.businessStore?.businessData;
  return (
    !(orderData.paymentInfo?.payments?.length > 0) &&
    businessData?.quickPaymentModes?.length > 0 &&
    !orderData.tags?.includes(COD)
  );
}

const checkForDiscountAndCheckout = (orderData: Record<string, any>) => {
  if (
    orderData.saleStatus?.toLowerCase() === OPEN ||
    orderData.saleStatus?.toLowerCase() === CLOSED
  ) {
    if (
      (orderData?.channel?.toLowerCase() === ONLINE &&
        orderData.fulfillmentStatus !== CREATED) ||
      orderData?.channel?.toLowerCase() !== ONLINE
    ) {
      return true;
    }
  }
  if (
    isPosChannel(orderData.channel) &&
    orderData.saleStatus !== CANCELLED &&
    !!orderData.paymentInfo?.balanceAmount &&
    orderData?.deliveryInfo?.mode
  ) {
    return true;
  }
  if (
    orderData?.channel?.toLowerCase() === ONLINE &&
    orderData.saleStatus?.toLowerCase() === OPEN &&
    orderData.fulfillmentStatus !== CREATED &&
    apiSupportArr.includes(orderData.sourceInfo?.source?.toLowerCase())
  )
    return true;
  if (checkForCodZomato(orderData)) {
    return true;
  }
  if (
    orderData.tags?.includes(COD) &&
    orderData?.channel?.toLowerCase() === ONLINE &&
    orderData.fulfillmentStatus !== CREATED
  ) {
    return true;
  }
  return false;
};

const checkForCodZomato = (orderData: any) => {
  if (orderData?.channel?.toLowerCase() === ONLINE) {
    if (isZomatoOrder(orderData)) {
      if (
        orderData.saleStatus?.toLowerCase() === OPEN &&
        orderData.fulfillmentStatus === ACCEPTED &&
        isOrderCod(orderData) &&
        orderData.channel?.toLowerCase() === ONLINE
      ) {
        return true;
      }
    }
  }
  return false;
};

function showZomatoSwiggyCta(orderData: Record<string, any> = {}) {
  const condition1 =
    isPosChannel(orderData?.channel) &&
    [DELIVERY, PICKUP].includes(orderData.deliveryInfo?.mode) &&
    orderData.fulfillmentStatus !== DELIVERED;
  const condition2 =
    isPosChannel(orderData?.channel) &&
    (orderData.saleStatus === CANCELLED ||  orderData.saleStatus === VOIDED) ;
  const condition3 =
    orderData?.channel?.toLowerCase() === ONLINE &&
    ![DELIVERED].includes(orderData.fulfillmentStatus);
  return condition1 || condition2 || condition3;
}

function checkWebhookOrder(orderData: Record<string, any>) {
  return (
    isOrderCod(orderData) &&
    orderData.saleStatus?.toLowerCase() === OPEN &&
    apiSupportArr.includes(orderData.sourceInfo?.source?.toLowerCase())
  );
}

function showInstoreCodCta(orderData: Record<string, any> = {}) {
  const deliveryCondition =
    orderData?.fulfillmentStatus !== DELIVERED &&
    isPosChannel(orderData?.channel) &&
    isOffline() &&
    [DELIVERY, PICKUP].includes(orderData.deliveryInfo?.mode);
  const deliveryCondition2 =
    (orderData.tags?.includes(COD) &&
      orderData?.channel?.toLowerCase() === ONLINE &&
      orderData.fulfillmentStatus !== CREATED) ||
    (isPosChannel(orderData.channel) &&
      (orderData.nextAction || orderData.fulfillmentStatus === DELIVERED));

  return deliveryCondition || deliveryCondition2;
}

const checkForEditBtn = (orderData: any) => {
  let condition1 =
    orderData.saleStatus?.toLowerCase() === OPEN &&
    orderData.channel?.toLowerCase() !== ONLINE;
  let condition2 =
    orderData?.channel?.toLowerCase() === ONLINE &&
    orderData.saleStatus?.toLowerCase() === OPEN &&
    apiSupportArr.includes(orderData.sourceInfo?.source?.toLowerCase()) &&
    !!orderData?.serviceLabel?.resourceId &&
    orderData.fulfillmentStatus !== CREATED;
  let condition3 =
    orderData.saleStatus?.toLowerCase() === OPEN &&
    orderData?.sourceInfo?.source?.toLowerCase() === SUPPORT;  
  let condition4 = 
    ((isPosChannel(orderData.channel) && orderData.saleStatus?.toLowerCase() === OPEN) ||
    checkAPISupportOrder(orderData)) && orderData?.sourceInfo?.isEditable;

  return condition1 || condition2 || condition3 || condition4 ;
};

function checkAPISupportOrder(orderData: Record<string, any> = {}) {
  return (
    orderData.channel?.toLowerCase() === ONLINE &&
    apiSupportArr.includes(orderData.sourceInfo?.source?.toLowerCase()) &&
    orderData.saleStatus?.toLowerCase() === OPEN &&
    (orderData.saleStatus?.toLowerCase() === OPEN ||
      orderData.saleStatus?.toLowerCase() === CLOSED)
  );
}

function showVoidCta(orderData: Record<string, any>) {

  let condition1 =
    orderData.channel?.toLowerCase() === ONLINE &&
    orderData.saleStatus?.toLowerCase() === CLOSED &&
    ![ZOMATO, SWIGGY].includes(orderData.sourceInfo?.source);
  const condition2 =
    isPosChannel(orderData.channel) &&
    orderData.saleStatus?.toLowerCase() === CLOSED;

  return condition1 || condition2;
}

export function checkAssignRiderCta(orderData: SaleDataType) {
  const { deliveryBy } = orderData;
  //fulfilment status check accoring to channel
  const statusCheckArr =
    orderData?.channel?.toLowerCase() === ONLINE
      ? assignRiderStatusesOnline
      : assignRiderStatusesPOS;
  return (
    isDAASOrderSource(orderData) &&
    !isRiderAssignAttempted(orderData) &&
    (!deliveryBy?.contactInfo ||
      Object.keys(deliveryBy?.contactInfo || {}).length == 0) &&
    statusCheckArr?.includes(orderData?.fulfillmentStatus || "")
  );
}

function isSupportOrder(orderData: SaleDataType) {
  return orderData?.sourceInfo?.source?.toLowerCase() === SUPPORT || orderData?.onlineSource?.toLowerCase() === SUPPORT;
}

function checkForCancelRider(orderData: Record<string, any>) {
  const { eventLog } = orderData,
    isAssginedOrCancelled =
      eventLog?.length &&
      eventLog.some(
        (obj: any, idx: number) =>
          obj.status === DAAS_CONSTANTS.RIDER_ASSIGNED ||
          obj.status === DAAS_CONSTANTS.RIDER_CANCELLED
      );
  return isRiderAssignAttempted(orderData) && !isAssginedOrCancelled && !isOffline();
}

function checkForFeedbackCta(orderData: Record<string, any>) {
  if (isOffline()) return false;
  if (isPosChannel(orderData.channel)) {
       if (
      orderData.saleStatus?.toLowerCase() === CLOSED ||
      orderData.saleStatus?.toLowerCase() === OPEN
    )
    {
      return true;
    }
  } else if (orderData.channel?.toLowerCase() === ONLINE) {
    if (
      swiggyZomatoArr.includes(
        orderData.sourceInfo?.source &&
          [DELIVERY, PICKUP].includes(orderData.deliveryInfo?.mode)
      )
    ) {
      return true;
    } else if (
      [DELIVERY, PICKUP].includes(orderData.deliveryInfo?.mode) &&
      (orderData.saleStatus?.toLowerCase() === CLOSED ||
        orderData.saleStatus?.toLowerCase() === OPEN)
    ) {
      return true;
    }
  }
  return false;
}

// The channel rule lives in itemLabelSettings/rules, shared with both apps' order-screen print
// handlers. `/rules`, not the barrel: the barrel pulls the registry, view model and hook behind it.
export const checkForPrintLabelSticker = (orderData: Record<string, any>) =>
  labelsEnabledForSale(store.getState().catalogStore?.posSettings?.itemLabelSettings, orderData);

// NON_CHARGEABLE_SALE lets the cashier apply NC outright; NON_CHARGEABLE_SALE_APPLY alone needs a
// manager's approval (`hooks/useNcApproval`). True only for the approval-gated case.
export const isNCSaleAllowed = (): boolean =>
  !featureRestrictions(NON_CHARGEABLE_SALE) && !!featureRestrictions(NON_CHARGEABLE_SALE_APPLY);
